import * as api from "../api/disciplio-api";
import {
  BrowserPushSetupError,
  createOrRefreshBrowserPushSubscription,
  needsAppleHomeScreenInstall,
  supportsBrowserPush,
  type BrowserPushSetupErrorCode,
  type BrowserPushSubscription,
} from "./browser-notifications";

export type BrowserPushEnableResult =
  | { ok: true; subscription: BrowserPushSubscription; replaced: boolean }
  | { ok: false; code: BrowserPushSetupErrorCode };

/**
 * Full enable path used by Account settings and the post-creation prompt.
 * Must be called from a user gesture so the browser can show its permission dialog.
 */
export async function enableBrowserPushSetup(): Promise<BrowserPushEnableResult> {
  if (!supportsBrowserPush()) {
    return { ok: false, code: "unsupported" };
  }
  if (needsAppleHomeScreenInstall()) {
    return { ok: false, code: "unsupported" };
  }

  try {
    console.info("[disciplio:web-push]", "config", { action: "load", source: "enableBrowserPushSetup" });
    const config = await api.getWebPushConfig();
    if (!config.available || !config.publicKey) {
      return { ok: false, code: "config_unavailable" };
    }

    const setup = await createOrRefreshBrowserPushSubscription(config.publicKey);
    if (setup.replaced && setup.previousEndpoint) {
      try {
        await api.deleteWebPushSubscription(setup.previousEndpoint);
      } catch (error) {
        console.error("[disciplio:web-push]", "persist", {
          name: error instanceof Error ? error.name : "Error",
          message: error instanceof Error ? error.message : String(error),
          action: "delete_stale",
        });
      }
    }

    console.info("[disciplio:web-push]", "persist", {
      action: "save",
      replaced: setup.replaced,
    });
    await api.saveWebPushSubscription(setup.subscription);
    return { ok: true, subscription: setup.subscription, replaced: setup.replaced };
  } catch (error) {
    if (error instanceof BrowserPushSetupError) {
      return { ok: false, code: error.code };
    }
    console.error("[disciplio:web-push]", "persist", {
      name: error instanceof Error ? error.name : "Error",
      message: error instanceof Error ? error.message : String(error),
    });
    return { ok: false, code: "save_failed" };
  }
}
